import React, { useEffect, useState } from "react";
import * as faceapi from "face-api.js";

const STORAGE_KEY = "student_game_progress_dynamic";
const shuffleArray = (arr) => [...arr].sort(() => Math.random() - 0.5);

export default function StudentGame() {
  const [imageList, setImageList] = useState([]);
  const [answered, setAnswered] = useState([]);
  const [score, setScore] = useState(0);
  const [currentImage, setCurrentImage] = useState(null);
  const [options, setOptions] = useState([]);
  const [isShuffling, setIsShuffling] = useState(false);
  const [shufflingImage, setShufflingImage] = useState(null);
  const [isFlipping, setIsFlipping] = useState(false);
  const [genderLogs, setGenderLogs] = useState([]);

  useEffect(() => {
    fetch("/images/list.json")
      .then((res) => res.json())
      .then((data) => {
        setImageList(data);
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY)) || {
          answered: [],
          score: 0,
        };
        setAnswered(saved.answered);
        setScore(saved.score);
      });

    Promise.all([
      faceapi.nets.tinyFaceDetector.loadFromUri("/models"),
      faceapi.nets.ageGenderNet.loadFromUri("/models"),
    ]).then(() => console.log("Face-api models loaded."));
  }, []);

  const detectGender = async (imagePath) => {
    const img = await faceapi.fetchImage(imagePath);
    const detection = await faceapi
      .detectSingleFace(img, new faceapi.TinyFaceDetectorOptions())
      .withAgeAndGender();
    return detection?.gender; // "male" or "female"
  };

  const saveProgress = (newAnswered, newScore) => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ answered: newAnswered, score: newScore })
    );
  };

  const doShuffle = async () => {
    if (answered.length >= imageList.length || imageList.length === 0) return;
    setIsShuffling(true);
    setIsFlipping(false);

    const remainingImages = imageList.filter((file) => {
      const name = file.split(".")[0];
      return !answered.includes(name);
    });

    let frame = 0;
    const totalFrames = 10;

    const interval = setInterval(() => {
      const temp =
        remainingImages[Math.floor(Math.random() * remainingImages.length)];
      setShufflingImage(temp);
      frame++;

      if (frame >= totalFrames) {
        clearInterval(interval);

        // Wrap the async logic inside an IIFE
        (async () => {
          const finalImage =
            remainingImages[Math.floor(Math.random() * remainingImages.length)];
          const correctName = finalImage.split(".")[0];
          const imagePath = `/images/${finalImage}`;
          const gender = await detectGender(imagePath);

          setGenderLogs((prevLogs) => [
            `${finalImage}: Detected as ${gender}`,
            ...prevLogs.slice(0, 20), // keep last 20 logs max
          ]);

          const otherNames = imageList
            .map((file) => file.split(".")[0])
            .filter((name) => name !== correctName);

          // Simple heuristic to match name to gender
          const genderMatch = (name) => {
            const femaleSuffixes = ["a", "i", "e", "y"];
            const isFemaleName = femaleSuffixes.includes(
              name.slice(-1).toLowerCase()
            );
            return gender === "female" ? isFemaleName : !isFemaleName;
          };

          const filteredNames = otherNames.filter(genderMatch);
          const wrongOptions = shuffleArray(filteredNames).slice(0, 3);
          const allOptions = shuffleArray([correctName, ...wrongOptions]);

          setTimeout(() => {
            setIsShuffling(false);
            setCurrentImage(finalImage);
            setOptions(allOptions);
            setIsFlipping(true);
            setTimeout(() => setIsFlipping(false), 1000);
          }, 100);
        })();
      }
    }, 70);
  };

  const handleAnswer = (selected) => {
    const correct = currentImage.split(".")[0];
    const isCorrect = selected === correct;

    if (isCorrect) {
      const newAnswered = [...answered, correct];
      const newScore = score + 1;
      setAnswered(newAnswered);
      setScore(newScore);
      saveProgress(newAnswered, newScore);
    }

    setTimeout(() => {
      setCurrentImage(null);
      setOptions([]);
      if (isCorrect) doShuffle();
    }, 300);
  };

  const handleReset = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAnswered([]);
    setScore(0);
    setCurrentImage(null);
    setOptions([]);
  };

  const imageStyle = {
    width: "200px",
    height: "200px",
    objectFit: "cover",
    borderRadius: "12px",
    transition: "transform 0.5s ease",
    backfaceVisibility: "hidden",
    boxShadow: "0 4px 20px rgba(0,0,0,0.2)"
  };

  const flipContainerStyle = {
    perspective: "1000px",
    width: "200px",
    height: "200px",
    margin: "auto",
    position: "relative", // Add this
  };

  const flipperStyle = {
    position: "relative",
    width: "100%",
    height: "100%",
    transformStyle: "preserve-3d",
    transition: "transform 0.6s",
    transform: isFlipping ? "rotateY(180deg)" : "rotateY(0deg)",
  };

  const frontBackStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backfaceVisibility: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <div className="container mt-5 text-center">
      <h2 className="mb-4">🎓 Guess the Student</h2>
      <p>
        Score: <strong>{score}</strong>
      </p>
  
      {/* Image Display */}
      <div className="d-flex justify-content-center align-items-center my-4">
        {isShuffling && shufflingImage && (
          <img
            src={`/images/${shufflingImage}`}
            alt="shuffling"
            className="img-fluid"
            style={{ ...imageStyle, transform: "scale(1.05) rotate(5deg)" }}
          />
        )}
  
        {!isShuffling && currentImage && (
          <div style={flipContainerStyle}>
            <div style={flipperStyle}>
              <img
                src={`/images/${currentImage}`}
                alt="Student"
                style={{ ...imageStyle, ...frontBackStyle }}
              />
              <div
                style={{
                  ...frontBackStyle,
                  transform: "rotateY(180deg)",
                  backgroundColor: "#f0f0f0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "24px",
                  color: "#666",
                }}
              >
                🤔
              </div>
            </div>
          </div>
        )}
      </div>
  
      {/* Answer Buttons */}
      <div className="row justify-content-center mt-3">
        {options.map((name) => (
          <div key={name} className="col-6 col-md-3 mb-2">
            <button
              className="btn btn-primary w-100"
              onClick={() => handleAnswer(name)}
              style={{ fontSize: "16px", padding: "8px" }}
            >
              {name.charAt(0).toUpperCase() + name.slice(1)}
            </button>
          </div>
        ))}
      </div>
  
      {/* Controls */}
      <div className="mt-4">
        <button
          className="btn btn-success me-2"
          onClick={doShuffle}
          disabled={isShuffling || answered.length >= imageList.length}
        >
          🔀 Shuffle
        </button>
        <button className="btn btn-danger" onClick={handleReset}>
          🔄 Reset
        </button>
      </div>
  
      {/* Completion Message */}
      {answered.length >= imageList.length && imageList.length > 0 && (
        <div className="mt-4 alert alert-info d-inline-block">
          🎉 You’ve guessed all students! Click reset to start over.
        </div>
      )}
  
      {/* Logs */}
      <div className="container mt-4">
  <div className="card">
    <div className="card-header">Gender Detection Log</div>
    <div className="card-body" style={{ maxHeight: "200px", overflowY: "auto", fontFamily: "monospace", fontSize: "12px" }}>
      <ul className="mb-0 ps-3">
        {genderLogs.map((log, idx) => (
          <li key={idx}>{log}</li>
        ))}
      </ul>
    </div>
    <div className="card-footer text-end">
      <button className="btn btn-sm btn-outline-secondary" onClick={() => setGenderLogs([])}>
        Clear Logs
      </button>
    </div>
  </div>
</div>

    </div>
  );
  
}
